import { t } from "i18next";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { generateUniqueId } from "../../../../../common/generateUniqueId";
import { nonNullableFilter } from "../../../../../common/nonNullableFilter";
import {
	createConnection,
	createDeviceSlot,
	insertMemory,
	insertMemoryRef,
	selectEditor,
	updateDeviceSlot,
} from "../../../../editor/editorSlice";
import Device, {
	createDevice,
} from "../../../../editor/interfaces/components/Device";
import Memory, {
	MemorySourceAddress,
} from "../../../../editor/interfaces/components/Memory";
import Operator from "../../../../editor/interfaces/components/Operator";
import { selectLogics } from "../../../../logics/logicsSlice";
import {
	closeWindow,
	WindowTypeAndPropsDefinition,
} from "../../../windowManagerSlice";
import css from "./SelectKeyWindow.module.css";

export interface SelectKeyWindowProps {
	preview: Operator;
}

export type SelectKeyWindowTypeAndProps = WindowTypeAndPropsDefinition<
	"selectKey",
	SelectKeyWindowProps
>;

export function SelectKeyWindow(props: SelectKeyWindowProps & { id: string }) {
	const { preview } = props;
	const dispatch = useAppDispatch();
	const editor = useAppSelector(selectEditor);
	const logics = useAppSelector(selectLogics);
	const [ICU, setICU] = useState<Device>();
	const [keyList, setKeyList] = useState<{ key: string; name: string }[]>([]);
	const [io, setIo] = useState<{ key: string; name: string }>();

	useEffect(() => {
		let device = editor.devices.find((device) => device?.label == "ICU820");

		if (!device) {
			device = createDevice("ICU820", logics);
			dispatch(createDeviceSlot());
			dispatch(updateDeviceSlot({ index: 0, device: device }));
		}

		setICU(device);

		const keys = device!.inputs.filter(
			(input) => input.name.includes("KEY") && input.name.includes("PRESS"),
		);

		const keyList = keys?.map((key) => {
			const chave = key.name.split("_");
			return {
				key: key.name,
				name: `${chave[0]}_${chave[1]}`,
			};
		});

		setKeyList(keyList);
		setIo(keyList[0]);
	}, [dispatch]);

	const createMemoryRef = (
		id: string,
		name: string,
		sourceId: string,
		index: number = 0,
	) => {
		const position =
			name == "INPUT"
				? {
						left: preview.boundingBox.left - 250,
						top: preview.boundingBox.top + index,
					}
				: {
						left: preview.boundingBox.left + 550,
						top: preview.boundingBox.top + index,
					};

		dispatch(
			insertMemoryRef({
				memoryRef: {
					id: id,
					name: name,
					sourceMemoryId: sourceId,
					sheetId: preview.sheetId,
					boundingBox: {
						...position,
						width: 0,
						height: 0,
					},
					error: false,
				},
			}),
		);
	};

	const createMemory = (
		category: "INPUT" | "VARIABLE" | "OUTPUT",
		action?: string,
	): string => {
		const newId = generateUniqueId("memory");
		const ioType = category == "INPUT" ? "inputs" : "outputs";
		const deviceInput = ICU?.[ioType].find(
			(input) => input.name == `${io!.name}_${action}`,
		);

		const variables = logics.memory[category]?.variables.reduce(
			(acc, variable) => {
				return {
					...acc,
					[variable.name]: {
						label: variable.label,
						content: {
							type: variable.content.type,
							value: variable.content.default,
						},
					},
				};
			},
			{},
		);

		const data = logics.memory[category]?.data?.reduce((acc, data) => {
			return {
				...acc,
				[data.size]: "",
			};
		}, {});

		const type = editor.memories[category]
			.map((memory) => memory.source)
			.filter(nonNullableFilter)
			.filter((source) => "address" in source) as MemorySourceAddress[];

		const existingAddresses = type
			.map((source) => source.address)
			.toSorted((a, b) => a - b);
		let address = existingAddresses.findIndex(
			(address, index) => address !== index,
		);

		if (address === -1) {
			address = existingAddresses.length;
		}

		const stageNumber = io?.name.split("_")[1];

		dispatch(
			insertMemory({
				memory: {
					id: newId,
					label:
						category !== "VARIABLE"
							? deviceInput!.name
							: `STAGE_${stageNumber}`,
					name: category,
					source:
						category !== "VARIABLE"
							? {
									type: "io",
									deviceId: ICU?.id,
									io: deviceInput!.name,
								}
							: {
									type: "address",
									deviceId: ICU?.id,
									address: address,
								},
					broadcast: false,
					metadata: {
						variables: variables,
						data: data,
					},
				} as Memory,
			}),
		);

		return newId;
	};

	const handleVariable = (index: number) => {
		const memoryId = createMemory("VARIABLE");
		const newId = generateUniqueId("memoryRef");
		createMemoryRef(newId, "VARIABLE", memoryId, index * 40);
		dispatch(
			createConnection({
				id: generateUniqueId("connection"),
				origin: {
					type: "operator",
					operatorId: preview.id,
					ioType: "output",
					ioGroup: index,
					ioIndex: 0,
				},
				destination: {
					memoryRefId: newId,
					type: "memoryRef",
					ioType: "input",
				},
			}),
		);
	};

	const handleKey = (
		foundMemory: Memory | undefined,
		index: number,
		action: string,
	) => {
		if (foundMemory) {
			const newId = generateUniqueId("memoryRef");
			createMemoryRef(newId, foundMemory.name, foundMemory.id, index * 40);
			dispatch(
				createConnection({
					id: generateUniqueId("connection"),
					origin: {
						type: "operator",
						operatorId: preview.id,
						ioType: "output",
						ioGroup: index,
						ioIndex: 0,
					},
					destination: {
						memoryRefId: newId,
						type: "memoryRef",
						ioType: "input",
					},
				}),
			);
		} else {
			const memoryId = createMemory("OUTPUT", action);
			const newId = generateUniqueId("memoryRef");
			createMemoryRef(newId, "OUTPUT", memoryId, index * 40);
			dispatch(
				createConnection({
					id: generateUniqueId("connection"),
					origin: {
						type: "operator",
						operatorId: preview.id,
						ioType: "output",
						ioGroup: index,
						ioIndex: 0,
					},
					destination: {
						memoryRefId: newId,
						type: "memoryRef",
						ioType: "input",
					},
				}),
			);
		}
	};

	const connectMemories = () => {
		if (!io) return;

		const memories = editor.memories;

		logics.operator[preview.name].inputs.forEach((input, index) => {
			if (input.defaultConnection === null) return;

			const foundMemory = memories.INPUT.find(
				(memory) =>
					memory.source.type == "io" &&
					memory.source.io == `${io.name}_${input.defaultConnection?.action}`,
			);

			if (foundMemory) {
				const newId = generateUniqueId("memoryRef");
				createMemoryRef(newId, foundMemory.name, foundMemory.id, index * 40);
				dispatch(
					createConnection({
						id: generateUniqueId("connection"),
						origin: {
							memoryRefId: newId,
							type: "memoryRef",
							ioType: "output",
						},
						destination: {
							type: "operator",
							operatorId: preview.id,
							ioType: "input",
							ioGroup: index,
							ioIndex: 0,
						},
					}),
				);
			} else {
				const memoryId = createMemory("INPUT", input.defaultConnection?.action);
				const newId = generateUniqueId("memoryRef");
				createMemoryRef(newId, "INPUT", memoryId, index * 40);
				dispatch(
					createConnection({
						id: generateUniqueId("connection"),
						origin: {
							memoryRefId: newId,
							type: "memoryRef",
							ioType: "output",
						},
						destination: {
							type: "operator",
							operatorId: preview.id,
							ioType: "input",
							ioGroup: index,
							ioIndex: 0,
						},
					}),
				);
			}
		});

		logics.operator[preview.name].outputs.forEach((output, index) => {
			if (!output.defaultConnection) return;

			const foundMemory = memories.OUTPUT.find((memory) => {
				return (
					memory.source.type == "io" &&
					memory.source.io == `${io.name}_${output.defaultConnection?.action}`
				);
			});

			switch (output.defaultConnection.io) {
				case "VARIABLE":
					handleVariable(index);
					break;
				case "OUTPUT":
					handleKey(foundMemory, index, output.defaultConnection?.action);
					break;
				default:
					break;
			}
		});

		dispatch(closeWindow(props.id));
	};

	return (
		<div className={css.content}>
			<label>{t("selectKey")}</label>
			<select
				className={css.selectKey}
				id="key-select"
				name="key-select"
				value={io?.key}
				onChange={(e) => {
					const found = keyList.find((k) => k.key == e.target.value);
					if (found) {
						setIo(found);
					}
				}}
			>
				{keyList?.map((k) => (
					<option key={k.key} value={k.key}>
						{k.name}
					</option>
				))}
			</select>

			<button onClick={() => connectMemories()}>Create</button>
		</div>
	);
}
